import './filtering_panel.css';
import { FilterIconSvg } from '../../../assets/icons.tsx';
import { Collapsible } from 'radix-ui';
import SearchBar from '../search_bar/search_bar.tsx';
import { useState } from 'react';
import { Select } from '@sensirion/mkt-sw-components';
import { useAzureAuth } from '../../../hooks/use_azure_auth.ts';

const SwTargetKeys = [
    'Raspberry Pi',
    'Arduino',
    'Embedded',
    'Python',
    'Nucleo',
];
const ReleaseStateKeys = ['PUB', 'ITL', 'PRV'];
const InterfaceKeys = ['I2C', 'UART'];

export type Filters = {
    release_state: 'all' | (typeof ReleaseStateKeys)[number];
    sw_target: 'all' | (typeof SwTargetKeys)[number];
    interface: 'all' | (typeof InterfaceKeys)[number];
    show_deprecated: boolean;
};

interface FilteringPanelProps {
    onSearchChangeCallback: (value: string) => void;
    filters: Filters;
    setFilters: (filters: Filters) => void;
    searchValue: string;
}

function FilteringPanel({
    onSearchChangeCallback,
    filters,
    setFilters,
    searchValue,
}: FilteringPanelProps) {
    const user = useAzureAuth();
    const platformOptions = [
        {
            id: 'all',
            text: 'All Platforms',
        },
        {
            id: 'Raspberry Pi',
            text: 'Raspberry-Pi',
        },
        {
            id: 'Arduino',
            text: 'Arduino',
        },
        {
            id: 'Embedded',
            text: 'Embedded',
        },
        {
            id: 'Python',
            text: 'Python',
        },
        {
            id: 'Nucleo',
            text: 'ST Nucleo',
        },
    ];

    const releaseStateOptions = [
        {
            id: 'all',
            text: 'All',
        },
        {
            id: 'PUB',
            text: 'Public',
        },
        {
            id: 'ITL',
            text: 'Internal',
        },
        {
            id: 'PRV',
            text: 'Preview',
        },
    ];

    const interfaceOptions = [
        {
            id: 'all',
            text: 'All',
        },
        {
            id: 'I2C',
            text: 'I2C',
        },
        {
            id: 'UART',
            text: 'UART',
        },
    ];

    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    function platformSelectOnChange(id: string) {
        if (['all', ...SwTargetKeys].includes(id)) {
            setFilters({ ...filters, sw_target: id });
        }
    }

    function releaseStateSelectOnChange(id: string) {
        if (['all', ...ReleaseStateKeys].includes(id)) {
            setFilters({ ...filters, release_state: id });
        }
    }

    function interfaceSelectOnChange(id: string) {
        if (['all', ...InterfaceKeys].includes(id)) {
            setFilters({ ...filters, interface: id });
        }
    }

    function deprecatedSelectOnChange(id: string) {
        setFilters({ ...filters, show_deprecated: id == 'yes' });
    }

    return (
        <>
            <Collapsible.Root
                className="driver_list__search_filter_area"
                open={filterPanelOpen}
                onOpenChange={setFilterPanelOpen}
            >
                <div className="driver_list__search_filter_area__row">
                    <SearchBar
                        onChangeCallback={onSearchChangeCallback}
                        value={searchValue}
                    />
                    <Collapsible.Trigger asChild>
                        <button
                            type="button"
                            className="driver_list__search_filter_area__filters_button"
                        >
                            <FilterIconSvg></FilterIconSvg>
                            <div className="driver_list__search_filter_area__filters_button__label">
                                {filterPanelOpen
                                    ? 'Hide Filters'
                                    : 'Show Filters'}
                            </div>
                        </button>
                    </Collapsible.Trigger>
                </div>

                <Collapsible.Content className="driver_list__search_filter_area__collapsible">
                    <div className="driver_list__search_filter_area__filters_panel">
                        <div className="driver_list__search_filter_area__filters_panel__filter">
                            <div className="driver_list__search_filter_area__filters_panel__filter_name">
                                Platform
                            </div>
                            <Select
                                id="select-platform"
                                items={platformOptions}
                                onChange={platformSelectOnChange}
                                value={filters.sw_target}
                            />
                        </div>
                        {user.userProps.username && (
                            <div className="driver_list__search_filter_area__filters_panel__filter">
                                <div className="driver_list__search_filter_area__filters_panel__filter_name">
                                    Release state
                                </div>
                                <Select
                                    id="select-release_state"
                                    items={releaseStateOptions}
                                    onChange={releaseStateSelectOnChange}
                                    value={filters.release_state}
                                />
                            </div>
                        )}
                        <div className="driver_list__search_filter_area__filters_panel__filter">
                            <div className="driver_list__search_filter_area__filters_panel__filter_name">
                                Interface
                            </div>
                            <Select
                                id="select-interface"
                                items={interfaceOptions}
                                onChange={interfaceSelectOnChange}
                                value={filters.interface}
                            />
                        </div>
                        <div className="driver_list__search_filter_area__filters_panel__filter">
                            <div className="driver_list__search_filter_area__filters_panel__filter_name">
                                Show deprecated
                            </div>
                            <Select
                                id="select-deprecated"
                                items={[
                                    { id: 'no', text: 'No' },
                                    { id: 'yes', text: 'Yes' },
                                ]}
                                onChange={deprecatedSelectOnChange}
                                value={filters.show_deprecated ? 'yes' : 'no'}
                                disabled={
                                    !['all', 'PUB'].includes(
                                        filters.release_state,
                                    )
                                }
                            />
                        </div>
                    </div>
                </Collapsible.Content>
            </Collapsible.Root>
        </>
    );
}

export default FilteringPanel;
